/**
 * Thin in-app banner for premium state (payment failed / low balance / out of days). Renders
 * nothing pre-launch, while the user still has to verify their email, inside the new-account quiet
 * window, or when there's nothing to say — safe to mount app-wide. Copy, thresholds AND the
 * silence rules all come from the server (src/lib/premium/premiumBanner.ts); this is purely
 * presentational, so there is exactly one place to reason about when a user gets asked for money.
 */
'use client'

import { useEffect, useState } from 'react'
import { useSession } from 'next-auth/react'
import { usePathname } from 'next/navigation'
import { AlertTriangle, Clock, Zap, TrendingUp } from 'lucide-react'
import SiteBanner, { type BannerTone } from '@/components/SiteBanner'
import type { PremiumBannerKind, PremiumBannerState } from '@/lib/premium/premiumBanner'

const STYLE: Record<Exclude<PremiumBannerKind, 'none'>, { tone: BannerTone; icon: typeof AlertTriangle }> = {
  payment_failed: { tone: 'red', icon: AlertTriangle },
  out_of_days:    { tone: 'amber', icon: Clock },
  low_balance:    { tone: 'blue', icon: Clock },
  // The only banner a paying subscriber can ever see. Blue, not amber: they cancelled on purpose and
  // nothing is wrong, we are just making sure the end date does not arrive as a surprise.
  expiring_no_renew: { tone: 'blue', icon: Clock },
  // Trial-ending reminder: informational, not an alarm. Blue + clock, like expiring_no_renew — a
  // trialing user is fine, we are just telling them when the charge lands so it is never a surprise.
  trial_ending: { tone: 'blue', icon: Clock },
  // Reactivation: "you have free days waiting". Amber + zap — an opportunity to notice, not a warning.
  activate_days: { tone: 'amber', icon: Zap },
  // Win-recap ("ask at the win"): the user's own alert ran up. Amber + trending-up, a positive
  // opportunity not a warning. The richer GREEN card is on /alerts via PremiumInlineAsk; this thin bar
  // is the fallback on pages that carry the full-width banner. Register: ask-at-the-win-scope-0905.
  win_recap: { tone: 'amber', icon: TrendingUp },
  // Required by the Record type only: first_session_trial NEVER renders as this bar (see the early return below).
  first_session_trial: { tone: 'amber', icon: Zap },
}

/**
 * `initial` = the state the root layout computed server-side (src/lib/premium/premiumBannerServer.ts), so the bar is
 * in the server HTML and never lands after first paint pushing the page down (register
 * signed-in-visitors-still-shift-sitewide-1103). Undefined = the server did not decide (signed out, or its read
 * failed or timed out): fetch as before.
 */
export default function PremiumBanner({ initial }: { initial?: PremiumBannerState }) {
  const { status } = useSession()
  const pathname = usePathname()
  const [state, setState] = useState<PremiumBannerState | null>(initial ?? null)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (status !== 'authenticated' || initial !== undefined) return
    let alive = true
    fetch('/api/user/premium-banner')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setState(d) })
      .catch(() => {})
    return () => { alive = false }
  }, [status, initial])

  // Fixed-header pages own their vertical geometry — a banner here would cover their header.
  if (pathname?.startsWith('/chart/') || pathname === '/alerts' || pathname === '/stream') return null
  if (!state || state.kind === 'none' || dismissed) return null
  // The homepage and the dashboard carry the richer <ActivateDaysCard/> for the activate_days state
  // (a one-click on-ramp, not just a link). Suppress the thin bar there so the two never double up;
  // the bar still covers activate_days on every other page.
  if (state.kind === 'activate_days' && (pathname === '/' || pathname === '/stock-market-today')) return null
  // The first-session trial offer is ONE calm card in the /alerts feed (PremiumInlineAsk), never a site-wide bar:
  // the owner allowed an exception to the 48h quiet rule, not a banner on every page (stripe-starts-happen-day0-1021).
  if (state.kind === 'first_session_trial') return null
  const s = STYLE[state.kind]

  return (
    <SiteBanner
      tone={s.tone}
      Icon={s.icon}
      title={state.title}
      message={state.message}
      cta={state.cta ? { label: state.cta.label, href: state.cta.href } : undefined}
      onDismiss={() => setDismissed(true)}
    />
  )
}
